import React, { useId } from "react";

import { format } from "@gamedev-web/gamedev-sdk-core";
import { HubAccessLevel } from "@gamedev-web/one-gaming-crm";
import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { Close, InfoSolid } from "@gaming/xds-react-js-icons";

import { Modal } from "../../../../components/Modal/Modal";
import { LoadingSpinner } from "../../../../components/Spinner";
import { useStrings } from "../../../../localization/localizationHelpers";
import type {
  AdminContact,
  AdminContactLifecycleAction,
  AdminInvitation,
} from "../../../../models/adminPage";
import { getHubAccessPermissionLabels } from "../../../../models/hubPermissions";
import { getAvatarClass, getMemberInitials } from "../../avatarHelpers";
import { InvitationRole } from "../InviteContactModal";
import * as roleStyles from "../InviteContactModal/InviteContactModal.module.css";
import * as styles from "./AdminActionConfirmationModal.module.css";

export type AdminActionConfirmation =
  | Readonly<{ action: AdminContactLifecycleAction; member: AdminContact }>
  | Readonly<{ action: "changeRole"; accessLevel: HubAccessLevel; member: AdminContact }>
  | Readonly<{ action: "transferPrimarySignatory"; member: AdminContact }>
  | Readonly<{ action: "resendInvitation"; member: AdminInvitation }>
  | Readonly<{ action: "revokeInvitation"; member: AdminInvitation }>;

type AdminActionConfirmationModalProps = Readonly<{
  confirmation: AdminActionConfirmation | null;
  isBusy?: boolean;
  onCancel: () => void;
  onConfirm: (confirmation: AdminActionConfirmation) => void;
  onEditInvitation?: (invitation: AdminInvitation) => void;
}>;

export const AdminActionConfirmationModal = ({
  confirmation,
  isBusy = false,
  onCancel,
  onConfirm,
  onEditInvitation,
}: AdminActionConfirmationModalProps) => {
  const titleId = useId();
  const [
    removeHubAccessLabel,
    removeHubAccessTitle,
    removeHubAccessDescription,
    removeFromCompanyTitle,
    removeFromCompanyDescription,
    removeMemberLabel,
    revokeInvitationLabel,
    revokeInvitationTitle,
    revokeInvitationDescription,
    gameConceptAdminLabel,
    gameManagerLabel,
    submittedConceptsOnlyLabel,
    cancelLabel,
    closeLabel,
    roleChangeTitle,
    roleChangeDescription,
    roleChangeConfirmLabel,
    manageAllSubmissionsLabel,
    manageOwnSubmissionsLabel,
    manageCompanyDetailsLabel,
    manageHubSettingsLabel,
    hubAccessLabel,
    resendInvitationLabel,
    resendInvitationConfirmation,
    editInvitationDetailsLabel,
    organizationEmailNoticeTitle,
    organizationEmailNoticeDescription,
    transferPrimarySignatoryTitle,
    transferPrimarySignatoryDescription,
    transferPrimarySignatoryConfirmLabel,
  ] = useStrings(
    "partnerAdminRemoveHubAccessLabel",
    "partnerAdminRemoveHubAccessTitle",
    "partnerAdminRemoveHubAccessConfirmation",
    "partnerAdminRemoveFromCompanyTitle",
    "partnerAdminRemoveFromCompanyConfirmation",
    "partnerAdminRemoveMemberLabel",
    "partnerAdminRevokeInvitationLabel",
    "partnerAdminRevokeInvitationTitle",
    "partnerAdminRevokeInvitationConfirmation",
    "partnerAdminGameConceptAdminAccessLabel",
    "partnerAdminGameManagerAccessLabel",
    "partnerAdminSubmittedConceptsOnlyAccessLabel",
    "partnerAdminInviteCancelLabel",
    "partnerAdminConfirmationCloseLabel",
    "partnerAdminRoleChangeTitle",
    "partnerAdminRoleChangeDescription",
    "partnerAdminRoleChangeConfirmLabel",
    "partnerAdminInviteManageAllSubmissionsLabel",
    "partnerAdminInviteManageOwnSubmissionsLabel",
    "partnerAdminInviteManageCompanyDetailsLabel",
    "partnerAdminInviteManageHubSettingsLabel",
    "partnerAdminInviteHubAccessLabel",
    "partnerAdminResendInvitationLabel",
    "partnerAdminResendInvitationConfirmation",
    "partnerAdminEditInvitationDetailsLabel",
    "partnerAdminInviteOrganizationEmailNoticeTitle",
    "partnerAdminInviteOrganizationEmailNoticeDescription",
    "partnerAdminTransferPrimarySignatoryConfirmationFormat",
    "partnerAdminTransferPrimarySignatoryConfirmationDescriptionFormat",
    "partnerAdminTransferPrimarySignatoryConfirmLabel"
  );

  if (!confirmation) {
    return null;
  }

  const { action, member } = confirmation;
  const isRoleChange = action === "changeRole";
  const isResendInvitation = action === "resendInvitation";
  const isPrimarySignatoryTransfer = action === "transferPrimarySignatory";
  const memberId =
    action === "revokeInvitation" || action === "resendInvitation"
      ? confirmation.member.invitationId
      : confirmation.member.contactId;
  const name = `${member.firstName} ${member.lastName}`.trim() || member.email;
  const accessLevel =
    action === "revokeInvitation" || action === "resendInvitation"
      ? confirmation.member.accessLevel
      : isRoleChange
      ? confirmation.accessLevel
      : member.hubRole ?? HubAccessLevel.User;
  const currentAccessLevel =
    action === "changeRole" ? confirmation.member.hubRole ?? HubAccessLevel.User : accessLevel;
  const detail = isPrimarySignatoryTransfer
    ? member.jobTitle
    : currentAccessLevel === HubAccessLevel.Admin
    ? gameConceptAdminLabel
    : currentAccessLevel === HubAccessLevel.GameManager
    ? gameManagerLabel
    : submittedConceptsOnlyLabel;
  const newRoleLabel =
    accessLevel === HubAccessLevel.Admin
      ? gameConceptAdminLabel
      : accessLevel === HubAccessLevel.GameManager
      ? gameManagerLabel
      : submittedConceptsOnlyLabel;
  const title = format(
    isRoleChange
      ? roleChangeTitle
      : isResendInvitation
      ? resendInvitationLabel
      : isPrimarySignatoryTransfer
      ? transferPrimarySignatoryTitle
      : action === "removeHubAccess"
      ? removeHubAccessTitle
      : action === "removeFromCompany"
      ? removeFromCompanyTitle
      : revokeInvitationTitle,
    isResendInvitation ? member.email : name
  );
  const description = isRoleChange
    ? roleChangeDescription
    : isResendInvitation
    ? format(resendInvitationConfirmation, member.email)
    : isPrimarySignatoryTransfer
    ? format(transferPrimarySignatoryDescription, name)
    : action === "removeHubAccess"
    ? removeHubAccessDescription
    : action === "removeFromCompany"
    ? removeFromCompanyDescription
    : revokeInvitationDescription;
  const confirmLabel = isRoleChange
    ? roleChangeConfirmLabel
    : isResendInvitation
    ? resendInvitationLabel
    : isPrimarySignatoryTransfer
    ? transferPrimarySignatoryConfirmLabel
    : action === "removeHubAccess"
    ? removeHubAccessLabel
    : action === "removeFromCompany"
    ? removeMemberLabel
    : revokeInvitationLabel;
  const permissionLabels = isRoleChange
    ? getHubAccessPermissionLabels(accessLevel, {
        manageAllSubmissions: manageAllSubmissionsLabel,
        manageOwnSubmissions: manageOwnSubmissionsLabel,
        manageCompanyDetails: manageCompanyDetailsLabel,
        manageHubSettings: manageHubSettingsLabel,
      })
    : [];

  return (
    <Modal isOpen onClose={isBusy ? () => undefined : onCancel} ariaLabelledBy={titleId}>
      <div className={styles.modalBody} aria-busy={isBusy}>
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <button
            type="button"
            className={styles.close}
            aria-label={closeLabel}
            disabled={isBusy}
            onClick={onCancel}
          >
            <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
          </button>
        </div>
        <p className={styles.description}>{description}</p>
        <div className={styles.memberCard}>
          <div className={`${styles.avatar} ${getAvatarClass(memberId)}`} aria-hidden="true">
            {getMemberInitials(member)}
          </div>
          <div className={styles.identity}>
            <strong className={styles.name}>{name}</strong>
            <span className={styles.email}>{member.email}</span>
          </div>
          <span className={styles.detail}>{detail}</span>
        </div>
        {isResendInvitation && member.email.trim().toLowerCase().endsWith(".onmicrosoft.com") && (
          <div className={roleStyles.notice} role="status">
            <Icon
              aria-hidden="true"
              className={roleStyles.noticeIcon}
              icon={InfoSolid}
              iconSize={IconSize.Custom}
            />
            <span className={roleStyles.noticeContent}>
              <strong>{organizationEmailNoticeTitle}</strong>
              <span>{organizationEmailNoticeDescription}</span>
            </span>
          </div>
        )}
        {isRoleChange && (
          <div
            className={`${roleStyles.roleOption} ${roleStyles.roleOptionSelected} ${roleStyles.assignedRole}`}
          >
            <InvitationRole
              hubAccessLabel={hubAccessLabel}
              option={{ label: newRoleLabel, permissionLabels }}
            />
          </div>
        )}
        <div className={styles.actions}>
          <BasicButton
            as="button"
            buttonType={ButtonType.Secondary}
            type="button"
            disabled={isBusy}
            onClick={onCancel}
          >
            {cancelLabel}
          </BasicButton>
          {action === "resendInvitation" && onEditInvitation && (
            <BasicButton
              as="button"
              buttonType={ButtonType.Secondary}
              type="button"
              disabled={isBusy}
              onClick={() => onEditInvitation(confirmation.member)}
            >
              {editInvitationDetailsLabel}
            </BasicButton>
          )}
          <BasicButton
            as="button"
            buttonType={ButtonType.Brand}
            className={
              isRoleChange || isResendInvitation || isPrimarySignatoryTransfer
                ? undefined
                : styles.destructiveButton
            }
            type="button"
            aria-label={confirmLabel}
            disabled={isBusy}
            onClick={() => onConfirm(confirmation)}
          >
            {confirmLabel}
          </BasicButton>
        </div>
        {isBusy && (
          <div className={styles.busyOverlay} role="status" aria-label={confirmLabel}>
            <LoadingSpinner />
          </div>
        )}
      </div>
    </Modal>
  );
};
